<template>
  <div ref="rootRef" class="proofreading-category-accordion">
    <div class="proofreading-category-accordion__header" :class="{
      'proofreading-category-accordion__header--open': isOpen,
      'proofreading-category-accordion__header--has-leading': hasLeading,
    }">
      <div class="proofreading-category-accordion__header__inner">
        <div v-if="hasLeading" class="proofreading-category-accordion__leading">
          <slot name="header-leading" />
        </div>
        <button type="button" class="proofreading-category-accordion__toggle" :aria-expanded="isOpen" @click="toggle">
          <slot name="header">
            <span class="proofreading-category-accordion__label">{{ label }}</span>
          </slot>
          <span class="proofreading-category-accordion__chevron" aria-hidden="true">⌃</span>
        </button>
      </div>
    </div>
    <div class="proofreading-category-accordion__content"
      :class="{ 'proofreading-category-accordion__content--open': isOpen }" :aria-hidden="!isOpen">
      <ul class="proofreading-category-accordion__list">
        <slot />
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, defineExpose, defineProps, nextTick, ref, useSlots,
} from 'vue';
import {
  scrollToElementTop,
  SCROLL_INTO_VIEW_DURATION_MS,
} from '@/lib/utils/proofreadingEditorUtil';

const props = defineProps<{
  label?: string;
  defaultOpen?: boolean;
  scrollToTopOnOpen?: boolean;
}>();

const slots = useSlots();
const hasLeading = computed(() => !!slots['header-leading']);

const rootRef = ref<HTMLElement | null>(null);
const isOpen = ref(props.defaultOpen ?? true);

function toggle() {
  isOpen.value = !isOpen.value;
  if (!isOpen.value || !props.scrollToTopOnOpen) return;

  nextTick(() => {
    if (!rootRef.value) return;
    scrollToElementTop(rootRef.value, SCROLL_INTO_VIEW_DURATION_MS);
  });
}

function open() {
  isOpen.value = true;
}

defineExpose({
  open,
  isOpen,
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-category-accordion {
  display: flex;
  flex-direction: column;
  gap: 0;

  &__header {
    z-index: 100;
    position: sticky;
    inset: 0;

    display: flex;
    align-items: stretch;
    width: 100%;
    background-color: var(--color-background-normal);
    color: var(--color-text-on-fixed-bg);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);

    padding-top: 12px;
    border-bottom-right-radius: var(--radius-sm);
    border-bottom-left-radius: var(--radius-sm);

    &__inner {
      z-index: 100;
      position: sticky;
      inset: 0;

      display: flex;
      align-items: stretch;

      background-color: var(--color-bg-dark);
      width: 100%;
      border-radius: var(--radius-sm);
      color: var(--color-text-on-fixed-bg);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
    }
  }

  &__leading {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding: 12px 0 12px 16px;
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    min-width: 0;
    padding: 12px 16px;
    border: none;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    text-align: left;
  }

  &__header--has-leading &__toggle {
    padding-left: 12px;
  }

  &__label {
    margin: 0;
  }

  &__chevron {
    display: inline-block;
    flex-shrink: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    line-height: 0.5;
    transition: transform 0.2s linear;
    transform-origin: 50% 30%;
    transform: rotate(180deg);
  }

  &__header--open &__chevron {
    transform: rotate(0deg);
  }

  &__content {
    display: grid;
    grid-template-rows: 0fr;
    padding-top: 0;

    transition: grid-template-rows 0.3s linear, padding-top 0.3s linear;

    &--open {
      grid-template-rows: 1fr;
      overflow: visible;

      .proofreading-category-accordion__list {
        overflow: visible;
      }

      :deep(.issue-item__header) {
        top: 44px;
        z-index: 2;
      }
    }
  }

  &__list {
    overflow: hidden;
    min-height: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
  }
}
</style>
